import React, { useState, useEffect } from "react";
import client from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Trophy, Ban, RotateCcw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../i18n/LangContext";
import { useReasonOrder } from "../hooks/useReasonOrder";

export const DISQUALIFY_REASONS = ["Overpriced", "Wrong location", "Poor condition", "Legal / paperwork", "Agent unresponsive", "Sold or withdrawn", "Low Airbnb demand", "Other"];

export const RankBadge = ({ rank, className = "" }) => (
  rank ? <span data-testid="rank-badge" className={`inline-flex items-center gap-1 rounded-full bg-amber-400 px-2 py-0.5 text-[11px] font-bold text-black ${className}`}><Trophy className="h-3 w-3" /> #{rank}</span> : null
);

export const DisqualifiedBadge = ({ reason, className = "" }) => (
  <span data-testid="disqualified-badge" className={`inline-flex items-center gap-1 rounded-full border border-white/15 bg-black/60 px-2 py-0.5 text-[11px] font-semibold text-white/80 ${className}`}><Ban className="h-3 w-3" /> {reason || "Disqualified"}</span>
);

export const RankSelect = ({ property, onChange, disabled }) => {
  const { t } = useLang();
  const set = async (v) => {
    try {
      const r = await client.post(`/properties/${property.id}/rank`, { rank: Number(v) });
      onChange(r.data);
      toast.success(Number(v) ? `Ranked #${v}` : "Removed from top 5");
    } catch (e) { toast.error("Couldn't rank", { description: e?.response?.data?.detail }); }
  };
  return (
    <Select value={String(property.rank || 0)} onValueChange={set} disabled={disabled}>
      <SelectTrigger data-testid="rank-select" className={`h-10 w-[150px] border-white/12 text-sm ${property.rank ? "bg-amber-400/15 text-amber-300 border-amber-400/30" : "bg-white/[0.03] text-white"}`}>
        <Trophy className="h-4 w-4" /><SelectValue />
      </SelectTrigger>
      <SelectContent className="glass-strong border-white/10">
        <SelectItem value="0" className="focus:bg-white/5">{t("Not ranked")}</SelectItem>
        {[1, 2, 3, 4, 5].map((n) => <SelectItem key={n} value={String(n)} data-testid={`rank-option-${n}`} className="focus:bg-white/5">Top 5 · #{n}</SelectItem>)}
      </SelectContent>
    </Select>
  );
};

export const DisqualifyDialog = ({ open, onOpenChange, onConfirm, title }) => {
  const { t } = useLang();
  const { reasons, frequent } = useReasonOrder();
  const [reason, setReason] = useState(DISQUALIFY_REASONS[0]);
  useEffect(() => { if (frequent[0]) setReason(frequent[0]); }, [frequent]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await onConfirm({ disposition: "disqualified", disqualifyReason: reason, disqualifyNote: note.trim() }); onOpenChange(false); setNote(""); toast.success("Disqualified — kept for reference"); }
    catch (e) { toast.error("Couldn't disqualify"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="disqualify-dialog" className="max-w-md glass-strong border-white/10" onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-white">{t("Disqualify")} {title ? <span className="text-muted-foreground">· {title}</span> : "this property"}</DialogTitle>
          <DialogDescription>It stays in your CRM with its notes and history — it just leaves the active pipeline and the top 5. You can restore it any time.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 pt-1">
          <div>
            <label className="text-xs text-muted-foreground">Why?</label>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {reasons.map((r) => (
                <button key={r} type="button" data-testid={`disqualify-reason-${r.replace(/[^a-z]/gi, "").toLowerCase()}`} onClick={() => setReason(r)}
                  className={`rounded-full border px-3 py-2 text-xs font-medium transition-colors ${reason === r ? "border-brand-red/50 bg-brand-red/15 text-white" : frequent.includes(r) ? "border-white/25 text-white hover:text-white" : "border-white/12 text-muted-foreground hover:text-white"}`}>{r}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Note (optional)</label>
            <Input data-testid="disqualify-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Asking 2.4M, comps say 1.8M" className="mt-1 h-11 bg-input/60 border-white/10 focus-visible:ring-brand-red" />
          </div>
          <Button data-testid="disqualify-confirm-btn" onClick={submit} disabled={busy} className="h-11 w-full bg-brand-red text-white hover:bg-brand-redDark">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Ban className="h-4 w-4" />} Disqualify
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const DisqualifyButton = ({ property, onPatch }) => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const restore = async () => {
    setBusy(true);
    try { await onPatch({ disposition: "active" }); toast.success("Back in the running"); } catch (e) { toast.error("Couldn't restore"); } finally { setBusy(false); }
  };
  if (property.disposition === "disqualified") {
    return (
      <Button data-testid="restore-property-btn" onClick={restore} disabled={busy} variant="outline" className="h-10 border-brand-green/40 bg-brand-green/10 text-brand-green hover:bg-brand-green/20">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />} Restore
      </Button>
    );
  }
  return (
    <>
      <Button data-testid="disqualify-property-btn" onClick={() => setOpen(true)} variant="outline" className="h-10 border-white/12 bg-white/[0.03] text-white hover:bg-white/[0.07]"><Ban className="h-4 w-4" /> {t("Disqualify")}</Button>
      <DisqualifyDialog open={open} onOpenChange={setOpen} onConfirm={onPatch} />
    </>
  );
};

export const DisqualifiedBanner = ({ property }) => (
  property.disposition === "disqualified" ? (
    <div data-testid="disqualified-banner" className="flex flex-wrap items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3">
      <span className="grid h-8 w-8 place-items-center rounded-full bg-white/10 text-white/80"><Ban className="h-4 w-4" /></span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-white">Disqualified · {property.disqualifyReason || "No reason given"}</p>
        {property.disqualifyNote && <p className="truncate text-xs text-muted-foreground">{property.disqualifyNote}</p>}
      </div>
      <span className="text-xs text-muted-foreground">Kept for reference — not in the active pipeline</span>
    </div>
  ) : null
);
